import { useCallback, useEffect, useMemo, useState } from "react";

import { useLabels } from "~/hooks/useLabels";
import { useBreakpoints } from "~/hooks/useBreakpoints";

import { SheetModal } from "../sheetModal/SheetModal";
import { Button } from "../button/Button";
import { DialogModal } from "../dialogModal/DialogModal";
import { Text } from "../text/Text";
import { TextInput } from "../textInput/TextInput";
import { Box } from "../box/Box";

export interface ShareModalProps {
  isOpen: boolean;
  shareUrl: string;
  onRequestClose?: () => void;
}

const DEFAULT_LABELS = {
  "share.title": "Share Page",
  "share.cta.copy": "Copy",
  "share.cta.copied": "Copied",
};

export function ShareModal({
  isOpen,
  shareUrl,
  onRequestClose,
}: ShareModalProps) {
  const { t } = useLabels({ fallback: DEFAULT_LABELS });

  const breakpoints = useBreakpoints();

  const [isCopied, setIsCopied] = useState(false);

  const handleCopyClick = useCallback(() => {
    navigator.clipboard.writeText(shareUrl);
    setIsCopied(true);
  }, [shareUrl]);

  useEffect(() => {
    setIsCopied(false);
  }, [shareUrl]);

  const content = useMemo(() => {
    return (
      <>
        <Box gap={"var(--size-2)"} direction="column">
          <Text size="lg" weight="semibold">
            {t("share.title")}
          </Text>

          <Box
            gap={breakpoints.lg ? 4 : 16}
            direction={breakpoints.lg ? "row" : "column"}
          >
            <TextInput style={{ flexGrow: "1" }} value={shareUrl} readOnly />
            <Button
              appearance="primary"
              fill={!breakpoints.lg}
              icon={isCopied ? "check" : "clone"}
              onClick={handleCopyClick}
            >
              {breakpoints.lg
                ? ""
                : isCopied
                  ? t("share.cta.copied")
                  : t("share.cta.copy")}
            </Button>
          </Box>
        </Box>
      </>
    );
  }, [breakpoints.lg, handleCopyClick, isCopied, shareUrl, t]);

  return breakpoints.lg ? (
    <DialogModal isOpen={isOpen} size="sm" onRequestClose={onRequestClose}>
      <DialogModal.Body>{content}</DialogModal.Body>
    </DialogModal>
  ) : (
    <SheetModal isOpen={isOpen} onRequestClose={onRequestClose}>
      <SheetModal.Body>{content}</SheetModal.Body>
    </SheetModal>
  );
}
